import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import { Switch } from "../../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../../components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import {
  Loader2, RefreshCcw, AlertTriangle, DollarSign, Package, Settings2,
  MessageSquare, Clock, Plus, Pencil, Trash2, ShieldAlert
} from "lucide-react";
import { toast } from "sonner";
import { API } from "../../App";

const getAuthConfig = () => {
  const token = localStorage.getItem("token");
  return { headers: token ? { Authorization: `Bearer ${token}` } : {} };
};

const fmt = (n) => (n === null || n === undefined ? "—" : Number(n).toLocaleString());
const fmtD = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : "—");

const PctBadge = ({ pct }) => {
  if (pct === null || pct === undefined) return <span className="text-muted-foreground text-xs">∞</span>;
  const cls = pct >= 100 ? "bg-red-500/15 text-red-500" : pct >= 90 ? "bg-orange-500/15 text-orange-500"
    : pct >= 75 ? "bg-amber-500/15 text-amber-600" : "bg-emerald-500/15 text-emerald-600";
  return <Badge className={`${cls} border-0 text-[10px]`}>{pct.toFixed(0)}%</Badge>;
};

/* ─────────────── Account detail modal ─────────────── */
const AccountDetailModal = ({ userId, open, onClose, packages, onChanged }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [adj, setAdj] = useState({ usage_type: "sms", amount: "", reason: "" });
  const [grantPkg, setGrantPkg] = useState("");
  const [grantReason, setGrantReason] = useState("");
  const [reconciling, setReconciling] = useState(false);

  const load = useCallback(() => {
    if (!userId) return;
    setLoading(true);
    axios.get(`${API}/admin/usage-billing/accounts/${userId}`, getAuthConfig())
      .then(res => setData(res.data))
      .catch(() => toast.error("Failed to load account usage"))
      .finally(() => setLoading(false));
  }, [userId]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const submitAdjust = async () => {
    if (!adj.amount || !adj.reason.trim()) return toast.error("Amount and reason are required");
    try {
      await axios.post(`${API}/admin/usage-billing/adjust`, {
        user_id: userId, usage_type: adj.usage_type,
        amount: parseFloat(adj.amount), reason: adj.reason,
      }, getAuthConfig());
      toast.success("Adjustment recorded (audited)");
      setAdj({ usage_type: "sms", amount: "", reason: "" });
      load(); onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Adjustment failed"); }
  };

  const submitGrant = async () => {
    if (!grantPkg || !grantReason.trim()) return toast.error("Package and reason are required");
    try {
      await axios.post(`${API}/admin/usage-billing/grant-topup`, {
        user_id: userId, package_id: grantPkg, reason: grantReason,
      }, getAuthConfig());
      toast.success("Top-up granted");
      setGrantPkg(""); setGrantReason("");
      load(); onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Grant failed"); }
  };

  const reconcile = async () => {
    setReconciling(true);
    try {
      const res = await axios.post(`${API}/admin/usage-billing/reconcile/${userId}`, {}, getAuthConfig());
      if (res.data.skipped) toast.info(`Skipped: ${res.data.reason}`);
      else toast.success(`Reconciled vs Twilio — diff SMS segments: ${res.data.diff?.sms_segments}, minutes: ${res.data.diff?.minutes}${res.data.flagged ? " (FLAGGED)" : ""}`);
      load(); onChanged?.();
    } catch (e) { toast.error("Reconciliation failed"); }
    finally { setReconciling(false); }
  };

  const u = data?.usage;
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="usage-account-detail-modal">
        <DialogHeader>
          <DialogTitle className="font-['Manrope']">Account Communication Usage</DialogTitle>
          <DialogDescription>Authoritative period usage, ledger, top-ups and audit trail.</DialogDescription>
        </DialogHeader>
        {loading || !data ? (
          <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
        ) : (
          <div className="space-y-4">
            <div className="flex flex-wrap gap-2 items-center justify-between">
              <p className="text-xs text-muted-foreground">
                {u.plan.name} · {fmtD(u.period.start)} – {fmtD(u.period.end)}
              </p>
              <Button size="sm" variant="outline" onClick={reconcile} disabled={reconciling} data-testid="reconcile-account-btn">
                {reconciling ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <RefreshCcw className="w-3 h-3 mr-1" />}
                Reconcile vs Twilio
              </Button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {["sms", "minutes"].map(t => (
                <div key={t} className="rounded-lg border border-border p-3">
                  <p className="text-xs text-muted-foreground mb-1">{t === "sms" ? "SMS" : "Minutes"}</p>
                  <p className="text-lg font-bold text-foreground">{fmt(u[t].used)} <span className="text-sm font-normal text-muted-foreground">/ {u[t].unlimited ? "∞" : fmt(u[t].allowance)}</span></p>
                  <p className="text-[11px] text-muted-foreground">{fmt(u[t].included)} included + {fmt(u[t].topup)} top-up · {fmt(u[t].remaining)} left</p>
                  <div className="mt-1 text-[11px] text-muted-foreground space-x-2">
                    {Object.entries(u[t].breakdown || {}).filter(([k, v]) => typeof v === "number" && v > 0 && !["count", "segments"].includes(k)).map(([k, v]) =>
                      <span key={k}>{k}: <span className="text-foreground">{fmt(Math.round(v * 10) / 10)}</span></span>)}
                  </div>
                </div>
              ))}
            </div>

            {data.by_number?.length > 0 && (
              <div className="rounded-lg border border-border p-3">
                <p className="text-xs font-medium text-muted-foreground mb-1">By phone number (recent ledger)</p>
                {data.by_number.map((r, i) => (
                  <div key={i} className="flex justify-between text-xs py-0.5">
                    <span className="text-muted-foreground">{r.number}</span>
                    <span className="text-foreground tabular-nums">SMS {fmt(r.sms_units)} · Min {fmt(r.minutes)} · {r.count} events</span>
                  </div>
                ))}
              </div>
            )}

            {u.topups?.length > 0 && (
              <div className="rounded-lg border border-border p-3">
                <p className="text-xs font-medium text-muted-foreground mb-1">Top-ups</p>
                {u.topups.map(t => (
                  <div key={t.topup_id} className="flex justify-between text-xs py-0.5">
                    <span className="text-muted-foreground">{t.name} · {fmtD(t.purchased_at)} · {t.source} <Badge className="ml-1 border-0 text-[9px] bg-muted text-muted-foreground">{t.status}</Badge></span>
                    <span className="text-foreground tabular-nums">{fmt(t.remaining)}/{fmt(t.quantity)} left · ${Number(t.price).toFixed(2)}</span>
                  </div>
                ))}
              </div>
            )}

            {data.reconciliations?.length > 0 && (
              <div className="rounded-lg border border-border p-3">
                <p className="text-xs font-medium text-muted-foreground mb-1">Reconciliation reports</p>
                {data.reconciliations.map(r => (
                  <div key={r.reconciliation_id} className="text-xs py-0.5 flex justify-between">
                    <span className="text-muted-foreground">{fmtD(r.created_at)} — Twilio: {fmt(r.twilio?.sms_segments)} seg / {fmt(r.twilio?.call_minutes)} min (cost ${((r.twilio?.sms_cost || 0) + (r.twilio?.voice_cost || 0)).toFixed(2)})</span>
                    <span className={r.flagged ? "text-red-500 font-medium" : "text-emerald-600"}>{r.flagged ? "FLAGGED" : "OK"} · Δseg {r.diff?.sms_segments} Δmin {r.diff?.minutes}</span>
                  </div>
                ))}
              </div>
            )}

            {data.blocks?.length > 0 && (
              <div className="rounded-lg border border-border p-3">
                <p className="text-xs font-medium text-muted-foreground mb-1">Blocked billable attempts</p>
                {data.blocks.slice(0, 8).map(b => (
                  <div key={b.block_id} className="text-xs text-muted-foreground py-0.5">
                    {fmtD(b.created_at)} · {b.usage_type} · {b.units} units · source {b.source}
                  </div>
                ))}
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="rounded-lg border border-border p-3 space-y-2">
                <p className="text-xs font-medium text-foreground">Manual adjustment (audited)</p>
                <div className="flex gap-2">
                  <Select value={adj.usage_type} onValueChange={(v) => setAdj(a => ({ ...a, usage_type: v }))}>
                    <SelectTrigger className="h-8 text-xs w-28" data-testid="adjust-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="sms">SMS</SelectItem><SelectItem value="minutes">Minutes</SelectItem></SelectContent>
                  </Select>
                  <Input type="number" placeholder="± amount" className="h-8 text-xs" value={adj.amount}
                    onChange={(e) => setAdj(a => ({ ...a, amount: e.target.value }))} data-testid="adjust-amount-input" />
                </div>
                <Input placeholder="Reason (required)" className="h-8 text-xs" value={adj.reason}
                  onChange={(e) => setAdj(a => ({ ...a, reason: e.target.value }))} data-testid="adjust-reason-input" />
                <Button size="sm" className="h-7 text-xs" onClick={submitAdjust} data-testid="adjust-submit-btn">Apply adjustment</Button>
              </div>
              <div className="rounded-lg border border-border p-3 space-y-2">
                <p className="text-xs font-medium text-foreground">Grant free top-up</p>
                <Select value={grantPkg} onValueChange={setGrantPkg}>
                  <SelectTrigger className="h-8 text-xs" data-testid="grant-pkg-select"><SelectValue placeholder="Select package" /></SelectTrigger>
                  <SelectContent>
                    {packages.map(p => <SelectItem key={p.package_id} value={p.package_id}>{p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Input placeholder="Reason (required)" className="h-8 text-xs" value={grantReason}
                  onChange={(e) => setGrantReason(e.target.value)} data-testid="grant-reason-input" />
                <Button size="sm" className="h-7 text-xs" onClick={submitGrant} data-testid="grant-submit-btn">Grant top-up</Button>
              </div>
            </div>

            {data.adjustments?.length > 0 && (
              <div className="rounded-lg border border-border p-3">
                <p className="text-xs font-medium text-muted-foreground mb-1">Adjustment audit log</p>
                {data.adjustments.slice(0, 8).map(a => (
                  <div key={a.adjustment_id} className="text-xs text-muted-foreground py-0.5">
                    {fmtD(a.created_at)} · {a.usage_type} {a.amount > 0 ? "+" : ""}{a.amount} · {a.reason} · by {a.admin_email}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

/* ─────────────── Package editor ─────────────── */
const emptyPkg = { name: "", usage_type: "sms", quantity: "", price: "", expiration: "rollover", active: true, plans: [] };

const PackagesTab = ({ packages, reload }) => {
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyPkg);
  const [saving, setSaving] = useState(false);

  const openNew = () => { setForm(emptyPkg); setEditing("new"); };
  const openEdit = (p) => { setForm({ ...p }); setEditing(p.package_id); };

  const save = async () => {
    if (!form.name || !form.quantity || form.price === "") return toast.error("Name, quantity and price are required");
    setSaving(true);
    try {
      const body = { ...form, quantity: parseFloat(form.quantity), price: parseFloat(form.price), sort_order: form.sort_order || 0 };
      if (editing === "new") await axios.post(`${API}/admin/usage-billing/packages`, body, getAuthConfig());
      else await axios.put(`${API}/admin/usage-billing/packages/${editing}`, body, getAuthConfig());
      toast.success("Package saved");
      setEditing(null); reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const deactivate = async (p) => {
    try {
      await axios.put(`${API}/admin/usage-billing/packages/${p.package_id}`, { active: !p.active }, getAuthConfig());
      reload();
    } catch { toast.error("Update failed"); }
  };

  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <Button size="sm" onClick={openNew} data-testid="pkg-new-btn"><Plus className="w-4 h-4 mr-1" /> New Package</Button>
      </div>
      <div className="rounded-xl border border-border overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted/40 text-xs text-muted-foreground">
            <tr>
              <th className="text-left p-2">Name</th><th className="text-left p-2">Type</th>
              <th className="text-right p-2">Quantity</th><th className="text-right p-2">Price</th>
              <th className="text-left p-2">Expiration</th><th className="text-left p-2">Status</th><th className="p-2" />
            </tr>
          </thead>
          <tbody>
            {packages.map(p => (
              <tr key={p.package_id} className="border-t border-border/60" data-testid={`pkg-row-${p.package_id}`}>
                <td className="p-2 text-foreground">{p.name}</td>
                <td className="p-2">{p.usage_type === "sms" ? <MessageSquare className="w-4 h-4 text-purple-500" /> : <Clock className="w-4 h-4 text-orange-500" />}</td>
                <td className="p-2 text-right tabular-nums">{fmt(p.quantity)}</td>
                <td className="p-2 text-right tabular-nums">${Number(p.price).toFixed(2)}</td>
                <td className="p-2 text-xs text-muted-foreground">{p.expiration === "period_end" ? "Period end" : "Rolls over"}</td>
                <td className="p-2">{p.active ? <Badge className="bg-emerald-500/15 text-emerald-600 border-0 text-[10px]">Active</Badge> : <Badge className="bg-muted text-muted-foreground border-0 text-[10px]">Inactive</Badge>}</td>
                <td className="p-2 text-right whitespace-nowrap">
                  <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => openEdit(p)} data-testid={`pkg-edit-${p.package_id}`}><Pencil className="w-3 h-3" /></Button>
                  <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => deactivate(p)} data-testid={`pkg-toggle-${p.package_id}`}><Trash2 className="w-3 h-3" /></Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent className="max-w-sm" data-testid="pkg-editor-dialog">
          <DialogHeader><DialogTitle>{editing === "new" ? "New Top-Up Package" : "Edit Package"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input placeholder="Name (e.g. 1,000 SMS)" value={form.name} onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))} data-testid="pkg-name-input" />
            <div className="grid grid-cols-2 gap-2">
              <Select value={form.usage_type} onValueChange={(v) => setForm(f => ({ ...f, usage_type: v }))}>
                <SelectTrigger data-testid="pkg-type-select"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="sms">SMS</SelectItem><SelectItem value="minutes">Minutes</SelectItem></SelectContent>
              </Select>
              <Select value={form.expiration} onValueChange={(v) => setForm(f => ({ ...f, expiration: v }))}>
                <SelectTrigger data-testid="pkg-exp-select"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="rollover">Rolls over</SelectItem><SelectItem value="period_end">Expires at period end</SelectItem></SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Input type="number" placeholder="Quantity" value={form.quantity} onChange={(e) => setForm(f => ({ ...f, quantity: e.target.value }))} data-testid="pkg-qty-input" />
              <Input type="number" placeholder="Price $" value={form.price} onChange={(e) => setForm(f => ({ ...f, price: e.target.value }))} data-testid="pkg-price-input" />
            </div>
            <div className="flex items-center justify-between">
              <span className="text-xs text-muted-foreground">Active</span>
              <Switch checked={!!form.active} onCheckedChange={(v) => setForm(f => ({ ...f, active: v }))} data-testid="pkg-active-switch" />
            </div>
            <Button className="w-full" onClick={save} disabled={saving} data-testid="pkg-save-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save package"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

/* ─────────────── Settings tab ─────────────── */
const SettingsTab = () => {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/admin/usage-billing/settings`, getAuthConfig())
      .then(res => setSettings(res.data)).catch(() => {});
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`${API}/admin/usage-billing/settings`, settings, getAuthConfig());
      setSettings(res.data);
      toast.success("Usage billing settings saved");
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  if (!settings) return <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>;
  const setS = (k, v) => setSettings(s => ({ ...s, [k]: v }));

  return (
    <div className="space-y-4 max-w-2xl" data-testid="usage-settings-tab">
      <div className="rounded-xl border border-border p-4 space-y-3">
        <p className="text-sm font-medium text-foreground">Overage policy (at 100% usage)</p>
        <Select value={settings.overage_policy} onValueChange={(v) => setS("overage_policy", v)}>
          <SelectTrigger className="w-full sm:w-72" data-testid="overage-policy-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="require_topup">Require top-up (block outbound)</SelectItem>
            <SelectItem value="balance_overage">Auto-bill from prepaid balance</SelectItem>
            <SelectItem value="allow">Allow (no enforcement)</SelectItem>
          </SelectContent>
        </Select>
        <p className="text-[11px] text-muted-foreground">Inbound customer messages and calls are always saved and never blocked.</p>
      </div>

      <div className="rounded-xl border border-border p-4 space-y-3">
        <p className="text-sm font-medium text-foreground">What counts toward the allowance</p>
        {[["count_outbound_sms", "Outbound SMS"], ["count_inbound_sms", "Inbound SMS"],
          ["count_outbound_minutes", "Outbound call minutes"], ["count_inbound_minutes", "Inbound call minutes"]].map(([k, label]) => (
          <div key={k} className="flex items-center justify-between">
            <span className="text-xs text-muted-foreground">{label}</span>
            <Switch checked={!!settings[k]} onCheckedChange={(v) => setS(k, v)} data-testid={`setting-${k}`} />
          </div>
        ))}
        <div className="border-t border-border/60 pt-2">
          {Object.entries(settings.count_sources || {}).map(([k, v]) => (
            <div key={k} className="flex items-center justify-between py-0.5">
              <span className="text-xs text-muted-foreground capitalize">{k} SMS</span>
              <Switch checked={!!v} onCheckedChange={(nv) => setS("count_sources", { ...settings.count_sources, [k]: nv })} data-testid={`setting-source-${k}`} />
            </div>
          ))}
        </div>
      </div>

      <div className="rounded-xl border border-border p-4 space-y-3">
        <p className="text-sm font-medium text-foreground">Counting rules</p>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <p className="text-[11px] text-muted-foreground mb-1">MMS weight (× per media message)</p>
            <Input type="number" step="0.1" value={settings.mms_weight}
              onChange={(e) => setS("mms_weight", parseFloat(e.target.value) || 1)} data-testid="setting-mms-weight" />
          </div>
          <div>
            <p className="text-[11px] text-muted-foreground mb-1">Minute rounding</p>
            <Select value={settings.minute_rounding} onValueChange={(v) => setS("minute_rounding", v)}>
              <SelectTrigger data-testid="setting-minute-rounding"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="ceil">Round up per call (Twilio-style)</SelectItem>
                <SelectItem value="exact">Exact seconds</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div>
          <p className="text-[11px] text-muted-foreground mb-1">Call-leg multipliers (billable legs per call type)</p>
          <div className="grid grid-cols-4 gap-2">
            {Object.entries(settings.call_leg_multipliers || {}).map(([k, v]) => (
              <div key={k}>
                <p className="text-[10px] text-muted-foreground capitalize">{k.replace("_", " ")}</p>
                <Input type="number" step="1" min="0" value={v}
                  onChange={(e) => setS("call_leg_multipliers", { ...settings.call_leg_multipliers, [k]: parseFloat(e.target.value) || 0 })}
                  data-testid={`setting-leg-${k}`} />
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="rounded-xl border border-border p-4 space-y-3">
        <p className="text-sm font-medium text-foreground">Alert thresholds (%)</p>
        <Input value={(settings.alert_thresholds || []).join(", ")}
          onChange={(e) => setS("alert_thresholds",
            e.target.value.split(",").map(x => parseInt(x.trim())).filter(x => !isNaN(x) && x > 0))}
          data-testid="setting-thresholds" />
        <p className="text-[11px] text-muted-foreground">Owners get an in-app notification + email once per threshold per billing period.</p>
        <div>
          <p className="text-[11px] text-muted-foreground mb-1">Auto top-up platform max ($/period)</p>
          <Input type="number" className="w-40" value={settings.auto_topup_max_monthly}
            onChange={(e) => setS("auto_topup_max_monthly", parseFloat(e.target.value) || 0)} data-testid="setting-auto-max" />
        </div>
      </div>

      <Button onClick={save} disabled={saving} data-testid="usage-settings-save-btn">
        {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null} Save settings
      </Button>
    </div>
  );
};

/* ─────────────── Main page ─────────────── */
export default function AdminUsageBilling() {
  const [accounts, setAccounts] = useState([]);
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detailUser, setDetailUser] = useState(null);
  const [reconcilingAll, setReconcilingAll] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [acc, pkg] = await Promise.all([
        axios.get(`${API}/admin/usage-billing/accounts`, getAuthConfig()),
        axios.get(`${API}/admin/usage-billing/packages`, getAuthConfig()),
      ]);
      setAccounts(acc.data.accounts || []);
      setPackages(pkg.data.packages || []);
    } catch { toast.error("Failed to load usage billing data"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const reconcileAll = async () => {
    setReconcilingAll(true);
    try {
      const res = await axios.post(`${API}/admin/usage-billing/reconcile-all`, {}, getAuthConfig());
      toast.success(`Reconciled ${res.data.reconciled} account(s) against Twilio`);
      load();
    } catch { toast.error("Reconcile-all failed"); }
    finally { setReconcilingAll(false); }
  };

  const flaggedCount = accounts.filter(a => a.over_limit || a.negative_margin || a.high_usage).length;

  return (
    <div className="space-y-4" data-testid="admin-usage-billing-page">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h1 className="text-xl font-bold text-foreground font-['Manrope']">Communication Usage</h1>
          <p className="text-xs text-muted-foreground">Authoritative SMS &amp; minutes usage per account, top-up billing, and Twilio reconciliation.</p>
        </div>
        <Button size="sm" variant="outline" onClick={reconcileAll} disabled={reconcilingAll} data-testid="reconcile-all-btn">
          {reconcilingAll ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <RefreshCcw className="w-3 h-3 mr-1" />}
          Reconcile all vs Twilio
        </Button>
      </div>

      {flaggedCount > 0 && (
        <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 flex items-center gap-2 text-sm text-amber-600" data-testid="usage-flagged-banner">
          <ShieldAlert className="w-4 h-4" /> {flaggedCount} account(s) need attention (over limit, high usage, or negative margin).
        </div>
      )}

      <Tabs defaultValue="accounts">
        <TabsList className="max-w-full overflow-x-auto justify-start">
          <TabsTrigger value="accounts" data-testid="tab-accounts"><DollarSign className="w-3 h-3 mr-1" /> Accounts</TabsTrigger>
          <TabsTrigger value="packages" data-testid="tab-packages"><Package className="w-3 h-3 mr-1" /> Top-Up Packages</TabsTrigger>
          <TabsTrigger value="settings" data-testid="tab-settings"><Settings2 className="w-3 h-3 mr-1" /> Usage Rules</TabsTrigger>
        </TabsList>

        <TabsContent value="accounts">
          {loading ? (
            <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
          ) : (
            <div className="rounded-xl border border-border overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/40 text-xs text-muted-foreground">
                  <tr>
                    <th className="text-left p-2">Account</th>
                    <th className="text-left p-2">Plan</th>
                    <th className="text-left p-2">Period</th>
                    <th className="text-right p-2">SMS</th>
                    <th className="text-center p-2">%</th>
                    <th className="text-right p-2">Minutes</th>
                    <th className="text-center p-2">%</th>
                    <th className="text-right p-2">Top-Up Rev</th>
                    <th className="text-right p-2">Provider Cost</th>
                    <th className="text-left p-2">Flags</th>
                  </tr>
                </thead>
                <tbody>
                  {accounts.map(a => (
                    <tr key={a.user_id} className="border-t border-border/60 hover:bg-muted/30 cursor-pointer"
                      onClick={() => setDetailUser(a.user_id)} data-testid={`usage-account-row-${a.user_id}`}>
                      <td className="p-2">
                        <p className="text-foreground font-medium">{a.business_name || a.name || a.email}</p>
                        <p className="text-[10px] text-muted-foreground">{a.email}</p>
                      </td>
                      <td className="p-2 text-xs text-muted-foreground">{a.plan_name}<br /><span className="text-[10px]">${a.plan_price}/mo</span></td>
                      <td className="p-2 text-[11px] text-muted-foreground whitespace-nowrap">{fmtD(a.period.start)} – {fmtD(a.period.end)}</td>
                      <td className="p-2 text-right tabular-nums text-xs">
                        {fmt(a.sms.used)} / {a.sms.included === -1 ? "∞" : fmt(a.sms.included + a.sms.topup)}
                        {a.sms.topup > 0 && <span className="text-emerald-500 text-[10px]"> (+{fmt(a.sms.topup)})</span>}
                      </td>
                      <td className="p-2 text-center"><PctBadge pct={a.sms.pct} /></td>
                      <td className="p-2 text-right tabular-nums text-xs">
                        {fmt(a.minutes.used)} / {a.minutes.included === -1 ? "∞" : fmt(a.minutes.included + a.minutes.topup)}
                        {a.minutes.topup > 0 && <span className="text-emerald-500 text-[10px]"> (+{fmt(a.minutes.topup)})</span>}
                      </td>
                      <td className="p-2 text-center"><PctBadge pct={a.minutes.pct} /></td>
                      <td className="p-2 text-right tabular-nums text-xs">${(a.topup_revenue || 0).toFixed(2)}</td>
                      <td className="p-2 text-right tabular-nums text-xs">{a.provider_cost === null ? "—" : `$${a.provider_cost.toFixed(2)}`}</td>
                      <td className="p-2 space-x-1 whitespace-nowrap">
                        {a.over_limit && <Badge className="bg-red-500/15 text-red-500 border-0 text-[9px]">Over limit</Badge>}
                        {a.high_usage && <Badge className="bg-orange-500/15 text-orange-500 border-0 text-[9px]">High usage</Badge>}
                        {a.negative_margin && <Badge className="bg-purple-500/15 text-purple-500 border-0 text-[9px]"><AlertTriangle className="w-2.5 h-2.5 mr-0.5" />Neg. margin</Badge>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </TabsContent>

        <TabsContent value="packages">
          <PackagesTab packages={packages} reload={load} />
        </TabsContent>

        <TabsContent value="settings">
          <SettingsTab />
        </TabsContent>
      </Tabs>

      <AccountDetailModal userId={detailUser} open={!!detailUser} onClose={() => setDetailUser(null)}
        packages={packages} onChanged={load} />
    </div>
  );
}
